import type { Attachment } from "@sidegigllc/api/client";
import * as z from "zod/mini";
import { parkingExpenseTypeSchema } from "./shared";

const locationAddressTypeSchema = z.enum(["PRIMARY", "SECONDARY"]);

export const ARRIVAL_PIN_LABEL_MAX_LENGTH = 60;

/** Where pros should go at a venue, e.g. a stadium staff gate; the pro app navigates here instead of the address. */
const arrivalPinSchema = z.object({
  latitude: z.number().check(z.gte(-90), z.lte(90)),
  longitude: z.number().check(z.gte(-180), z.lte(180)),
  label: z.optional(z.string().check(z.maxLength(ARRIVAL_PIN_LABEL_MAX_LENGTH))),
});
export type ArrivalPin = z.infer<typeof arrivalPinSchema>;

export const locationAddressSchema = z.object({
  id: z.number(),
  type: locationAddressTypeSchema,
  street_address: z.string(),
  city: z.string(),
  state_code: z.string(),
  postal_code: z.string(),
  timezone: z.nullish(z.string()),
  name: z.nullish(z.string()),
  coordinates: z.nullish(
    z.object({
      type: z.optional(z.literal("Point")),
      coordinates: z.optional(z.array(z.number())),
    }),
  ),
  parking_expense_type: z.nullish(parkingExpenseTypeSchema),
  parking_instructions: z.nullish(z.string()),
  attachments: z.nullish(z.readonly(z.array(z.custom<Attachment>()))),
  arrival_pin: z.nullish(arrivalPinSchema),
  upcoming_shifts_count: z.nullish(z.number()),
});
export type LocationAddress = z.infer<typeof locationAddressSchema>;

export const locationAddressInputSchema = z.object({
  street_address: z.string(),
  city: z.string(),
  state_code: z.string(),
  postal_code: z.string(),
  name: z.pipe(
    z.nullish(z.string()),
    z.transform((v) => v ?? undefined),
  ),
  parking_expense_type: z.pipe(
    z.nullish(parkingExpenseTypeSchema),
    z.transform((v) => v ?? undefined),
  ),
  parking_instructions: z.pipe(
    z.nullish(z.string()),
    z.transform((v) => v ?? undefined),
  ),
  attachment_ids: z.pipe(
    z.nullish(z.array(z.string())),
    z.transform((v) => v ?? undefined),
  ),
  // null removes the pin, so unlike the fields above it must reach the API as null.
  arrival_pin: z.nullish(arrivalPinSchema),
});
export type LocationAddressInput = z.infer<typeof locationAddressInputSchema>;
